// Main Page for viewing reports details

import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import ReportHeader from "../components/reports/layout/ReportHeader";
import ReportCriteria from "../components/reports/layout/ReportCriteria";
import ReportResults from "../components/reports/layout/ReportResults";

import { useReportBuilder } from "../hooks/reports/useReportBuilder";
import { useReportBuilderStore } from "../store/report/useReportBuilderStore";

import "./ReportBuilder.css";
import { isNotFoundError, useReportDefinitionQuery } from "../hooks/reports/queries/useReportQueries";
import Loader from "../components/UI/Loader";
import { useReportMutations } from "../hooks/reports/mutations/useReportMutations";
import { useRefreshLock } from "../hooks/reports/useRefreshLock";

const ReportBuilderPage = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshToken, setRefreshToken] = useState(0);

  // Custom hooks
  const { runMutation } = useReportMutations();
  const {
    data: reportDefinition,
    isLoading: isDefinitionLoading,
    isFetching: isDefintionFetching,
    isError: isDefinitionError,
    error: definitionError,
  } = useReportDefinitionQuery(reportId || "");
  const {
    resetWorkflow,
    loadWorkflow,
    getFlattenedParameters,
    setInitialParameters,
  } = useReportBuilder();
  // The singleton report view is locked when the report's parent group
  // is refreshing. Standalone reports (no group) are never locked by
  // dashboard refresh.
  const { isLocked } = useRefreshLock({ groupId: reportDefinition?.reportGroupId ?? undefined });

  // State from the reports store to control UI behavior
  const reportType = useReportBuilderStore((s) => s.reportType);
  const steps = useReportBuilderStore((s) => s.steps);

  // LOCAL STATE (Previously in global store)
  const [showResults, setShowResults] = useState(false);
  const [resultsExpanded, setResultsExpanded] = useState(false);
  const [criteriaExpanded, setCriteriaExpanded] = useState(true);
  const [reportTypeExpanded, setReportTypeExpanded] = useState(true);
  const hydratedReportId = useRef<string>();

  useEffect(() => {
    document.title = reportId ? "Report Workspace" : "Create New Report";
  }, [reportId]);

  // A dashboard or another browser tab can delete a report after this route has
  // already been selected. Replace the stale URL as soon as its 404 is known.
  useEffect(() => {
    if (isDefinitionError && isNotFoundError(definitionError)) {
      navigate("/reports/new", { replace: true });
    }
  }, [definitionError, isDefinitionError, navigate]);

  useEffect(() => {
    if (!reportId || reportId === "new") {
      hydratedReportId.current = undefined;
      const urlType = searchParams.get("type");

      if (urlType) {
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          if (key !== "type") params[key] = value;
        });

        loadWorkflow(urlType, params);
      } else {
        resetWorkflow();
        setShowResults(false);
        setResultsExpanded(false);
        setCriteriaExpanded(true);
        setReportTypeExpanded(true);
      }
      return;
    }

    if (
      reportDefinition &&
      !isDefintionFetching &&
      hydratedReportId.current !== reportId
    ) {
      hydratedReportId.current = reportId;
      loadWorkflow(reportDefinition.reportType, reportDefinition.parameters);
      setCriteriaExpanded(false);
      setShowResults(true);
      setResultsExpanded(true);
    }
  }, [
    reportId,
    reportDefinition,
    isDefintionFetching,
    resetWorkflow,
    loadWorkflow,
  ])

  useEffect(() => {
    if (!reportId || reportId === "new") {
      const newParams = new URLSearchParams();

      if (reportType) {
        newParams.set("type", reportType);
        const currentParams = getFlattenedParameters();
        Object.entries(currentParams).forEach(([key, value]) => {
          if (value) newParams.set(key, value);
        });
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [reportType, steps, getFlattenedParameters, setSearchParams, reportId]);

  const handleRunReport = async (mode: "new" | "draft" | "refresh") => {
    const params = getFlattenedParameters();
    const response = await runMutation.mutateAsync({
      reportType,
      parameters: params,
      mode,
      reportId: mode === "new" ? undefined : reportId,
    });

    if (response) {
      setInitialParameters(params);
      if (mode === "new" || mode === "draft") {
        // Reset the detail pane when switching to the completed draft.
        setRefreshToken((current) => current + 1);
        navigate(`/reports/${response.reportId}`);
      } else if (mode === "refresh")
        setRefreshToken((current) => current + 1);
      
      setCriteriaExpanded(false);
      setShowResults(true);
      setResultsExpanded(true);
    }
  };

  if (isDefinitionLoading) {
    return (
      <div className="centered-container">
        <Loader />
      </div>
    );
  }

  if (isDefinitionError) {
    return (
      <div className="centered-container padding-4 center-text">
        <i className="ri-error-warning-line ri-4x text-danger margin-bottom-2" />
        <h2>Report Not Found</h2>
        <p>The report you are looking for does not exist, or you do not have permission to view it.</p>
        <button className="button primary" onClick={() => navigate("/reports/new")}>
          Create New Report
        </button>
      </div>
    );
  }

  return (
    <>
      <ReportHeader isLocked={isLocked} />
      <ReportCriteria
        onRunReport={handleRunReport}
        isRunning={runMutation.isPending}
        runError={runMutation.error}
        criteriaExpanded={criteriaExpanded}
        setCriteriaExpanded={setCriteriaExpanded}
        reportTypeExpanded={reportTypeExpanded}
        setReportTypeExpanded={setReportTypeExpanded}
        isLocked={isLocked}
      />
      {showResults && reportId && (
        <ReportResults
          onRefresh={() => handleRunReport("refresh")}
          resultsExpanded={resultsExpanded}
          setResultsExpanded={setResultsExpanded}
          setCriteriaExpanded={setCriteriaExpanded}
          isLocked={isLocked}
          isRunning={runMutation.isPending}
          refreshToken={refreshToken}
        />
      )}
    </>
  );
};

export default ReportBuilderPage;
